import dayjs, { Dayjs } from "dayjs"
import isSameOrAfter from "dayjs/plugin/isSameOrAfter"
import isSameOrBefore from "dayjs/plugin/isSameOrBefore"
import { IMeterpoint, MeterpointType } from "./meterpoint.types"

dayjs.extend(isSameOrAfter)
dayjs.extend(isSameOrBefore)
import {
  getDccStatus,
  getDccStatusWithLabel,
  getLatestMeterReadings,
  getLatestReadStatus,
  getLatestSmartReadStatus,
  getSupplyStatus,
  getTradMeterSupplyStatus,
  isWithinCancellationPeriod,
} from "@/helpers/meterpointHelper"
import {
  ELEC_FIRST_READ_WINDOW_END_OFFSET,
  ELEC_FIRST_READ_WINDOW_START_OFFSET,
  GAS_FIRST_READ_WINDOW_END_OFFSET,
  GAS_FIRST_READ_WINDOW_START_OFFSET,
  GSP_MAP,
} from "@/constants/meterpoints"
import { Status } from "@/types/Status"
import { IReading } from "../meterReading/meterReading.types"
import { IRegister } from "../meterRegister/meterRegister.types"
import { IMeter } from "../meter/meter.types"

const activeSupplyStatuses = [
  "RegistrationAccepted",
  "Registered",
  "RegistrationConfirmed",
  "RegistrationCompleted",
  "RegistrationRequested",
  "LossObjectionRaised",
  "LossObjected",
  "LossObjectionRaised",
  "LossObjectionUpheld",
]

export class Meterpoint implements IMeterpoint {
  constructor(
    public id: number,
    public identifier: string,
    public meterpointType: MeterpointType,
    public supplyStatus: string,
    public supplyStartDate: Dayjs,
    public operationType: string,
    public serviceType: string,
    public meters: IMeter[],
    public gsp?: string,
    public profileClass?: number,
    public dno?: string,
    public dcc?: string,
    public mam?: string,
    public mop?: string,
    public dataAvailable: boolean = true,
  ) {}

  get isActive() {
    return activeSupplyStatuses.includes(this.supplyStatus)
  }

  get isWithinCancellationPeriod() {
    return isWithinCancellationPeriod(this.supplyStartDate)
  }

  inFirstReadPeriod(currentDate = dayjs()) {
    const currentDateNormalized = dayjs(currentDate)

    return (
      currentDateNormalized.isSameOrAfter(this.firstReadWindowStart) &&
      currentDateNormalized.isSameOrBefore(this.firstReadWindowEnd)
    )
  }

  get latestRead(): IReading {
    return (
      getLatestMeterReadings(this.meters).sort((a, b) =>
        a.date.isAfter(b.date) ? 1 : -1,
      )[0] || {}
    )
  }

  get latestSmartRead(): IReading {
    return getLatestMeterReadings(this.meters, true).sort((a, b) =>
      a.date.isAfter(b.date) ? 1 : -1,
    )[0]
  }

  get status(): Status {
    return getSupplyStatus(this.supplyStatus)
  }

  get tradMeterStatus(): Status {
    return getTradMeterSupplyStatus(this.supplyStatus)
  }

  get dccStatus(): Status {
    return getDccStatus(this.dcc)
  }

  get dccStatusWithLabel(): Status {
    return getDccStatusWithLabel(this.dcc)
  }

  get latestReadStatus(): Status {
    return getLatestReadStatus(this.meters)
  }

  get latestSmartReadStatus(): Status {
    return getLatestSmartReadStatus(this.meters)
  }

  get gspText(): string {
    return GSP_MAP.get(this.gsp ?? "") || ""
  }

  get hasSmartMeter(): boolean {
    return this.meters.some((meter) => meter.isSmartMeter)
  }

  /** A smart meter that is installed and actively communicating via the DCC, not just installed hardware. */
  get hasWorkingSmartMeter(): boolean {
    return this.hasSmartMeter && this.dcc?.toLowerCase() === "active"
  }

  get hasSmets2(): boolean {
    return this.meters.some((meter) => meter.isSmet2)
  }

  get hasUnknownMeter(): boolean {
    return (
      this.meters.length > 0 &&
      this.meters.every((meter) => meter.isUnknownMeter)
    )
  }

  get displayDescription(): string {
    let meterKind: string
    if (this.hasUnknownMeter) {
      meterKind = "Unknown"
    } else if (this.hasSmartMeter) {
      meterKind = "Smart"
    } else {
      meterKind = "Trad"
    }
    const electricityOrGasString = this.isElectricity ? "electricity" : "gas"

    return `${meterKind} ${electricityOrGasString} meter`
  }

  get registers(): IRegister[] {
    return this.meters.reduce<IRegister[]>(
      (registers, meter) => [...registers, ...meter.registers],
      [],
    )
  }

  get readings(): IReading[] {
    return this.registers.reduce<IReading[]>(
      (allReadings, register) => [...allReadings, ...register.readings],
      [],
    )
  }

  get isExport(): boolean {
    return this.profileClass === 8
  }

  get isElectricity(): boolean {
    return this.meterpointType === "MPAN"
  }

  get eacOrAq(): string {
    return this.isElectricity ? "EAC" : "AQ"
  }

  get registerUsageFormatted(): Array<string> {
    return this.registers.map((register) => {
      if (register.rateName && register.rateName !== "Standard") {
        return register.rateName + ": " + register.usage + "kWh"
      } else {
        return register.usage + "kWh"
      }
    })
  }

  get hasTwoRateMeter(): boolean {
    return this.registers.some(
      (register) => register.rateName && register.rateName !== "Standard",
    )
  }

  get totalUsage(): number {
    return this.registers.reduce((acc, reg) => acc + reg.usage, 0)
  }

  get firstReadWindowStart(): Dayjs {
    const startOffset = this.isElectricity
      ? ELEC_FIRST_READ_WINDOW_START_OFFSET
      : GAS_FIRST_READ_WINDOW_START_OFFSET

    return this.supplyStartDate.subtract(startOffset, "day")
  }

  get firstReadWindowEnd(): Dayjs {
    const endOffset = this.isElectricity
      ? ELEC_FIRST_READ_WINDOW_END_OFFSET
      : GAS_FIRST_READ_WINDOW_END_OFFSET

    return this.supplyStartDate.add(endOffset, "day")
  }

  amendUsage(usage: Record<number, number>) {
    const newMeters = this.meters.map((meter) => {
      const newRegisters = meter.registers.map((register: IRegister) => {
        const updatedUsage = usage[register.id] ?? register.usage
        return { ...register, usage: updatedUsage }
      })
      return { ...meter, registers: newRegisters }
    })

    return new Meterpoint(
      this.id,
      this.identifier,
      this.meterpointType,
      this.supplyStatus,
      this.supplyStartDate,
      this.operationType,
      this.serviceType,
      newMeters,
      this.gsp,
      this.profileClass,
      this.dno,
      this.dcc,
      this.mam,
      this.mop,
      this.dataAvailable,
    )
  }
}
